import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { useTheme } from '../../../contexts/ThemeContext'
import type { AlderSeries } from './api'
import { seriesLabel } from './api'

export type DisplayMode = 'antall' | 'andel'

interface Props {
  series: AlderSeries[]
  loading: boolean
  title: string
  mode: DisplayMode
  /**
   * Labels that should be pulled out of the line chart and rendered as
   * side-stat badges above it instead. Used by the buyer-age view to push
   * `<18 år` and `90+ år` out of the main 18..89 curve.
   */
  outlierLabels?: string[]
}

// Stable colors per line. The backend caps a klasse split at 5 lines (#528) and
// merke-only at 5, so index 0 is always the aggregate and the palette never
// wraps in practice — the extra entries are headroom, not a second cycle.
const SERIES_COLORS = [
  '#0ea5e9', '#8b5cf6', '#06b6d4', '#f97316',
  '#10b981', '#ef4444', '#eab308', '#ec4899',
]

const fmtCount = (n: number) => n.toLocaleString('nb-NO')
const fmtPct = (n: number) =>
  n.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' %'

export default function AgeBucketChart({ series, loading, title, mode, outlierLabels }: Props) {
  const { t } = useTranslation('bruktbil')
  const { isDark } = useTheme()
  const axisColor = isDark ? '#94a3b8' : '#64748b'
  const gridColor = isDark ? '#334155' : '#e2e8f0'

  const outliers = useMemo(() => new Set(outlierLabels ?? []), [outlierLabels])

  // Total across all series — used to decide if there's anything to render
  // and to show in the title row.
  const grandTotal = series.reduce((sum, s) => sum + s.total, 0)

  // For each outlier label, summed count across all series and per-series breakdown.
  // Rendered as small badges above the chart so the main line stays focused on
  // the dense 18..89 range.
  const outlierStats = useMemo(() => {
    if (series.length === 0 || outliers.size === 0) return []
    const labels = series[0].buckets.filter(b => outliers.has(b.label)).map(b => b.label)
    return labels.map(label => {
      const total = series.reduce(
        (sum, s) => sum + (s.buckets.find(b => b.label === label)?.count ?? 0),
        0,
      )
      const share = grandTotal > 0 ? (total / grandTotal) * 100 : 0
      return { label, total, share }
    })
  }, [series, outliers, grandTotal])

  // Build a row-per-bucket dataset where each series contributes one column.
  // Bucket labels are taken from the first series — backend guarantees that
  // every series has the same bucket layout. Outlier labels are excluded.
  const chartData = useMemo(() => {
    if (series.length === 0) return []
    const labels = series[0].buckets.map(b => b.label).filter(l => !outliers.has(l))
    return labels.map(label => {
      const row: Record<string, number | string> = { label }
      for (const s of series) {
        const key = seriesLabel(s.merke, s.klasse)
        const bucket = s.buckets.find(b => b.label === label)
        const count = bucket?.count ?? 0
        if (mode === 'andel') {
          row[key] = s.total > 0 ? Math.round((count / s.total) * 1000) / 10 : 0
        } else {
          row[key] = count
        }
      }
      return row
    })
  }, [series, mode, outliers])

  if (loading) {
    return (
      <div data-testid="age-bucket-chart" className="card p-4 animate-pulse h-72 bg-gray-100 dark:bg-dark-border rounded-sm" />
    )
  }

  if (grandTotal === 0) {
    return (
      <div data-testid="age-bucket-chart" className="card p-4 text-sm text-gray-500">
        {t('aldersprofil.ingenData')}
      </div>
    )
  }

  const yTickFormatter = mode === 'andel'
    ? (v: number) => `${v.toFixed(0)} %`
    : (v: number) => v.toLocaleString('nb-NO')

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null
    return (
      <div className="rounded-lg shadow-lg border p-3 text-xs bg-white dark:bg-dark-surface dark:border-dark-border">
        <p className="font-semibold mb-1.5 text-gray-900 dark:text-gray-100">{label}</p>
        <div className="space-y-1">
          {payload.map((p: { name: string; value: number; color: string }) => (
            <div key={p.name} className="flex justify-between gap-4">
              <span style={{ color: p.color }}>● {p.name}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {mode === 'andel' ? fmtPct(p.value) : fmtCount(p.value)}
              </span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // For 50+ buckets (buyer-age 18..89) tick labels would collide; thin them out.
  const xTickInterval = chartData.length > 30 ? Math.ceil(chartData.length / 20) : 0

  return (
    <div data-testid="age-bucket-chart" className="card p-4">
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">{title}</h3>
        <span className="text-xs text-gray-500 tabular-nums">
          {t('aldersprofil.nEierskifter', { n: fmtCount(grandTotal) })}
        </span>
      </div>
      {outlierStats.length > 0 && (
        <div
          data-testid="outlier-badges"
          className="flex gap-2 flex-wrap mb-3"
        >
          {outlierStats.map(o => (
            <div
              key={o.label}
              data-testid={`outlier-badge-${o.label}`}
              className="inline-flex items-baseline gap-2 px-3 py-1.5 rounded-md bg-gray-50 dark:bg-dark-border/40 border border-gray-200 dark:border-dark-border"
            >
              <span className="text-xs text-gray-500 dark:text-gray-400">{o.label}</span>
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 tabular-nums">
                {fmtCount(o.total)}
              </span>
              <span className="text-xs text-gray-500 tabular-nums">
                {fmtPct(o.share)}
              </span>
            </div>
          ))}
        </div>
      )}
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridColor} />
          <XAxis
            dataKey="label"
            tick={{ fill: axisColor, fontSize: 11 }}
            tickLine={{ stroke: axisColor }}
            axisLine={{ stroke: axisColor }}
            interval={xTickInterval}
          />
          <YAxis
            tick={{ fill: axisColor, fontSize: 11 }}
            tickLine={{ stroke: axisColor }}
            axisLine={{ stroke: axisColor }}
            tickFormatter={yTickFormatter}
            allowDecimals={mode === 'andel'}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: 12, color: axisColor }} iconType="circle" />
          {series.map((s, idx) => {
            const label = seriesLabel(s.merke, s.klasse)
            return (
              <Line
                key={label}
                type="monotone"
                dataKey={label}
                name={label === 'Alle' ? t('common.alle') : label}
                stroke={SERIES_COLORS[idx % SERIES_COLORS.length]}
                strokeWidth={2}
                dot={{ r: 2 }}
                activeDot={{ r: 5 }}
                connectNulls
              />
            )
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
